/**
 * Live ↔ Dev backend switch — the successor to the legacy panel's env switcher.
 *
 * Only the DATA backend (wpcursor-backend) is tiered: main :3000 vs dev :3010.
 * When 'dev', every `/api/...` call is rewritten to `/api-dev/...`, which the
 * proxy (and nginx in prod) routes to the dev tier.
 *
 * Identity (wpcursor-admin-api) is NOT tiered — one service, one token. Because
 * that token verifies against either backend tier with the same public key,
 * switching environments needs NO re-login. That's the improvement over the old
 * panel, which kept a separate session per tier and forced a fresh sign-in.
 */

import { mcpEnvOf } from './mcpEnv';

const KEY = 'wpcAdminEnv';
export type Env = 'live' | 'dev';

function currentHost(): string {
  // `|| ''`: a stubbed or partial location (tests, some webviews) may have no hostname.
  return (typeof location !== 'undefined' && location.hostname) || '';
}

/**
 * Beta Admin has no switch. Its admin-api (portal-api-beta) signs with its own
 * key, which only the beta backend trusts, so a Dev tier there could only fail.
 * Every data call stays on that host's own /api/ — the beta backend.
 */
export function envSwitchable(host: string = currentHost()): boolean {
  return mcpEnvOf(host) !== 'beta';
}

export function getEnv(host: string = currentHost()): Env {
  if (!envSwitchable(host)) return 'live';
  const stored = localStorage.getItem(KEY);
  if (stored === 'live') return 'live';
  if (stored === 'dev') return 'dev';
  return defaultEnv(host);
}

/**
 * Default tier by host — until the tiers are ever split onto separate portals,
 * both stay switchable on either portal via the Live/Dev pills; only the landing
 * default differs. The prod portal (portal.wpos.ai) lands on Live; the dev portal
 * (portal.dev.wpos.ai) and localhost land on Dev. The prod backend now accepts
 * portal tokens, so Live-by-default on prod is safe.
 */
function defaultEnv(host: string): Env {
  return host === 'portal.wpos.ai' ? 'live' : 'dev';
}

export function setEnv(env: Env): void {
  localStorage.setItem(KEY, env === 'live' ? 'live' : 'dev');
}

/** Rewrites a data-backend path for the active tier. Portal paths are untouched. */
export function applyEnv(path: string, host: string = currentHost()): string {
  if (getEnv(host) === 'dev' && path.startsWith('/api/')) {
    return '/api-dev/' + path.slice('/api/'.length);
  }
  return path;
}
